import { newPlatformReviewItems, type NewPlatformTrackerItem } from '../../data/newPlatformTracker';
import { RANKING_LAST_VERIFIED_LABEL } from '../../data/rankingFreshness';
import { generalChatAgeNote } from '../../lib/reviewSafety';
import { ReviewPost } from '../../types/review';
import { getPlatformRating } from '../../utils/bestAIGirlfriendRatings';

const reviewDates = [
  '2026-10-26',
  '2026-11-04',
  '2026-10-26',
  '2026-10-20',
  '2026-10-15',
  '2026-10-14',
  '2026-10-08',
  '2026-10-05',
  '2026-10-01',
  '2026-10-01',
  '2026-09-25',
  '2026-09-22',
  '2026-09-22',
  '2026-09-22',
  '2026-09-22',
  '2026-09-13',
  '2026-09-04',
  '2026-09-01',
  '2026-08-29',
  '2026-08-29',
  '2026-08-10',
  '2026-08-10',
  '2026-08-10',
  '2026-08-10',
  '2026-08-10',
  '2026-08-10',
  '2026-08-10',
  '2026-08-10',
  '2026-08-10',
  '2026-08-10',
  '2026-07-28',
  '2026-07-28',
  '2026-07-28',
  '2026-07-28',
  '2026-07-27',
  '2026-07-27',
  '2026-07-27',
  '2026-07-27',
  '2026-07-26',
  '2026-07-26',
  '2026-07-13',
  '2026-07-13',
  '2026-07-13',
  '2026-07-13',
  '2026-07-07',
  '2026-07-06',
  '2026-07-05',
  '2026-07-04',
  '2026-07-03',
  '2026-07-02',
  '2026-07-01',
];

const toListItems = (items: string[]) =>
  items.map((item) => `<li>${item}</li>`).join('\n');

const GENERAL_CHAT_STORE_RATINGS: Record<string, string> = {
  'polybuzz-ai-review': '18+',
  'talkie-ai-review': '18+',
};

const reviewScore = (item: NewPlatformTrackerItem) => {
  const datasetScore = getPlatformRating(item.reviewSlug);
  return datasetScore > 0 ? datasetScore : item.signalScore;
};

const HEAVEN_AI_AFFILIATION =
  'HeavenAI (heavenai.app) is a third-party app. It is not owned by, run by or affiliated with Heaven Girlfriend.';

const buildReviewContent = (item: NewPlatformTrackerItem) => {
  const scoreLabel = reviewScore(item).toFixed(2);
  return `
  ${
    item.reviewSlug === 'heaven-ai-review'
      ? `<p class="mb-6">${HEAVEN_AI_AFFILIATION}</p>`
      : ''
  }
  <p class="mb-6">
    ${
      GENERAL_CHAT_STORE_RATINGS[item.reviewSlug]
        ? `<strong>${item.productName}</strong> is covered here as a general chat and roleplay app, not as an adult companion. This first-look review focuses on the product promise, visible feature set, and what still needs hands-on verification.`
        : `<strong>${item.productName}</strong> is a new-to-Heaven Girlfriend platform we are tracking because it fits the AI girlfriend and romantic companion category more closely than broad AI assistant tools. This first-look review focuses on the product promise, visible feature set, user fit, and what still needs hands-on verification before it can compete with our main ranked platforms.`
    }
  </p>
  ${
    GENERAL_CHAT_STORE_RATINGS[item.reviewSlug]
      ? `<p class="mb-6">${generalChatAgeNote(item.productName, GENERAL_CHAT_STORE_RATINGS[item.reviewSlug])}</p>`
      : ''
  }

  <h2 id="what-it-is" class="text-2xl font-bold text-pink-400 mb-4">What ${item.productName} Is</h2>
  <p class="mb-6">
    ${item.reviewSummary}
  </p>

  <h2 id="best-fit" class="text-2xl font-bold text-pink-400 mb-4">Who ${item.productName} Is Best For</h2>
  <p class="mb-6">
    ${item.bestFor}
  </p>

  <h2 id="features" class="text-2xl font-bold text-pink-400 mb-4">${item.productName} Features</h2>
  <ul class="list-disc list-inside mb-6 space-y-2 text-pink-200">
    ${toListItems(item.features)}
  </ul>

  <h2 id="what-stands-out" class="text-2xl font-bold text-pink-400 mb-4">What Stands Out</h2>
  <ul class="list-disc list-inside mb-6 space-y-2 text-pink-200">
    ${toListItems(item.standoutPoints)}
  </ul>

  <h2 id="what-to-check" class="text-2xl font-bold text-pink-400 mb-4">What We Still Need To Check</h2>
  <ul class="list-disc list-inside mb-6 space-y-2 text-pink-200">
    ${toListItems(item.watchPoints)}
  </ul>

  ${item.price && !/to verify/i.test(item.price) ? `<h2 id="pricing" class="text-2xl font-bold text-pink-400 mb-4">${item.productName} Pricing</h2>
  <p class="mb-6">
    Current pricing signal: <strong>${item.price}</strong>. Pricing, credits, free limits, and renewal behavior should be checked directly before making a final value judgment.
  </p>
  <p class="mb-6 text-sm text-pink-200/70">Prices checked ${RANKING_LAST_VERIFIED_LABEL}.</p>` : ''}

  <h2 id="verdict" class="text-2xl font-bold text-pink-400 mb-4">${item.productName}: Early Verdict (${scoreLabel}/5)</h2>
  <p class="mb-6">
    ${item.productName} earns an early Heaven Girlfriend signal score of <strong>${scoreLabel}/5</strong>. It belongs on the new AI girlfriend tracker because it has a real companion use case, but it should be treated as a first-look review until chat quality, image quality, billing, privacy, and safety controls are tested in depth.
  </p>
`;
};

const buildRatingCategories = (score: number) => [
  { name: 'AI Girlfriend Fit', score },
  { name: 'Feature Set', score: Math.max(3.7, score - 0.05) },
  { name: 'Visual Potential', score: Math.max(3.7, score - 0.1) },
  { name: 'User Experience Signal', score: Math.max(3.7, score - 0.15) },
  { name: 'Value Signal', score: Math.max(3.6, score - 0.2) },
];

const HANDS_ON_REVIEW_SLUGS = new Set([
  'joi-ai-review',
  'camsoda-ai-review',
  'cherrypop-ai-review',
  'eternal-ai-review',
]);

const newAiPlatformReviews: ReviewPost[] = newPlatformReviewItems.map((item, index) => {
  const score = reviewScore(item);
  return {
  id: 1100 + index,
  slug: item.reviewSlug,
  title: item.reviewTitle,
  excerpt: item.reviewExcerpt,
  content: buildReviewContent(item),
  image: item.image,
  date: reviewDates[index] ?? '2026-07-01',
  ...(item.reviewSlug === 'polybuzz-ai-review' || item.reviewSlug === 'talkie-ai-review'
    ? { reviewDate: 'October 15, 2026' }
    : item.reviewSlug === 'heaven-ai-review'
      ? { reviewDate: 'October 26, 2026' }
      : {}),
  readTime: item.readTime,
  category: item.category,
  featured: false,
  authorSlug: 'bernardo-barbosa',
  metaTitle: `${item.productName} Review 2026`,
  metaDescription: item.reviewExcerpt,
  productName: item.productName,
  rating: score,
  schemaRating: score,
  schemaItemName: item.productName,
  schemaItemReviewedType: 'SoftwareApplication' as const,
  ratingCategories: buildRatingCategories(score),
  pros: item.standoutPoints,
  cons: item.watchPoints,
  features: item.features,
  testimonials: [],
  affiliateLink: item.platformUrl,
  price: item.price,
  label: item.description,
  verdict: item.reviewExcerpt,
  keyPros: item.standoutPoints.slice(0, 3),
  keyCons: item.watchPoints.slice(0, 3),
  homePagePros: item.standoutPoints.slice(0, 3).map((point) => point.split(' ').slice(0, 4).join(' ')),
  homePageCons: item.watchPoints.slice(0, 3).map((point) => point.split(' ').slice(0, 4).join(' ')),
};
}).filter((review) => !HANDS_ON_REVIEW_SLUGS.has(review.slug));

export default newAiPlatformReviews;
